import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import Icon from '../components/Icon';
import { getCircuitImageUrl } from '../components/CircuitMap';

const SEASON = new Date().getFullYear();
const CANCELLED_RACES = new Set(['Bahrain', 'Saudi Arabia']);

const COUNTRY_FLAGS = {
  'Australia': '🇦🇺', 'China': '🇨🇳', 'Japan': '🇯🇵', 'Bahrain': '🇧🇭',
  'Saudi Arabia': '🇸🇦', 'Miami': '🇺🇸', 'Emilia Romagna': '🇮🇹', 'Monaco': '🇲🇨',
  'Spain': '🇪🇸', 'Canada': '🇨🇦', 'Austria': '🇦🇹', 'Great Britain': '🇬🇧',
  'United Kingdom': '🇬🇧', 'Hungary': '🇭🇺', 'Belgium': '🇧🇪', 'Netherlands': '🇳🇱',
  'Italy': '🇮🇹', 'Azerbaijan': '🇦🇿', 'Singapore': '🇸🇬', 'United States': '🇺🇸',
  'Mexico': '🇲🇽', 'Brazil': '🇧🇷', 'Las Vegas': '🇺🇸', 'Qatar': '🇶🇦', 'Abu Dhabi': '🇦🇪',
};
const getFlag = (country) => {
  const key = Object.keys(COUNTRY_FLAGS).find(k => country?.includes(k) || k.includes(country));
  return key ? COUNTRY_FLAGS[key] : '🏁';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const formatDateLong = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' });
};

const isCancelled = (race) => CANCELLED_RACES.has(race.circuit?.country);
const isCompleted = (race) => !!(race.isLocked && race.results?.winner);
const isUpcoming = (race) => {
  if (isCancelled(race) || race.isLocked) return false;
  const d = race.sessions?.race?.date;
  return !d || new Date(d) > new Date();
};

// Live countdown hook
const useCountdown = (targetDate) => {
  const [diff, setDiff] = useState(targetDate ? targetDate - Date.now() : null);
  useEffect(() => {
    if (!targetDate) return;
    const t = setInterval(() => setDiff(targetDate - Date.now()), 1000);
    return () => clearInterval(t);
  }, [targetDate]);
  if (!diff || diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s };
};

// Next race hero
const NextRaceHero = ({ race }) => {
  const raceTs = race.sessions?.race?.date ? new Date(race.sessions.race.date).getTime() : null;
  const countdown = useCountdown(raceTs);
  const circuitUrl = getCircuitImageUrl(race.circuit?.country, race.circuit?.circuitName);
  const flag = getFlag(race.circuit?.country);

  return (
    <Link to={`/grand-prix/${race._id}`}
      className="block card relative overflow-hidden mb-6 group hover:border-zinc-600 transition-all"
      style={{ background: 'linear-gradient(135deg, #18181b 60%, #e1060008)' }}>

      {/* Circuit image */}
      {circuitUrl && (
        <img src={circuitUrl} alt="" aria-hidden
          className="absolute right-0 top-0 h-full w-1/2 object-contain object-right pointer-events-none select-none opacity-20 group-hover:opacity-30 transition-opacity"
          style={{ filter: 'brightness(1.5)' }} />
      )}
      {/* Red glow top-left */}
      <div className="absolute top-0 left-0 w-40 h-40 pointer-events-none rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(225,6,0,0.12) 0%, transparent 70%)' }} />

      <div className="relative z-10">
        {/* Label */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-f1red">Prochain Grand Prix</span>
          <span className="text-[10px] bg-f1red/15 text-f1red border border-f1red/30 px-2 py-0.5 rounded-full font-bold animate-pulse">LIVE</span>
        </div>

        {/* Title */}
        <div className="flex items-start gap-3 mb-4">
          <span className="text-3xl leading-none mt-1">{flag}</span>
          <div>
            <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest mb-0.5">Round {race.round} · {SEASON}</p>
            <h2 className="text-2xl font-black text-white leading-tight group-hover:text-f1red transition-colors">{race.raceName}</h2>
            <p className="text-zinc-500 text-sm mt-0.5">{race.circuit?.circuitName}</p>
          </div>
        </div>

        {/* Countdown */}
        {countdown ? (
          <div className="flex gap-3 mb-4">
            {[['J', countdown.d], ['H', countdown.h], ['M', countdown.m], ['S', countdown.s]].map(([label, val]) => (
              <div key={label} className="text-center bg-zinc-900/80 rounded-xl px-3 py-2 border border-zinc-800 min-w-[52px]">
                <p className="text-2xl font-black text-white tabular-nums leading-none">{String(val).padStart(2, '0')}</p>
                <p className="text-[9px] text-zinc-500 uppercase tracking-widest mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-zinc-500 text-sm mb-4">Course bientôt</p>
        )}

        {/* Sessions */}
        <div className="flex flex-wrap gap-3 text-xs">
          {race.sessions?.qualifying?.date && (
            <span className="flex items-center gap-1.5 text-zinc-400">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 shrink-0" />
              Qualifs · {formatDate(race.sessions.qualifying.date)}
            </span>
          )}
          {race.sessions?.sprint?.date && (
            <span className="flex items-center gap-1.5 text-zinc-400">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
              Sprint · {formatDate(race.sessions.sprint.date)}
            </span>
          )}
          {race.sessions?.race?.date && (
            <span className="flex items-center gap-1.5 text-zinc-400">
              <span className="w-1.5 h-1.5 rounded-full bg-f1red shrink-0" />
              Course · {formatDateLong(race.sessions.race.date)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

// Race card
const RaceCard = ({ race, idx }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const cancelled = isCancelled(race);
  const done = isCompleted(race);
  const circuitUrl = getCircuitImageUrl(race.circuit?.country, race.circuit?.circuitName);
  const flag = getFlag(race.circuit?.country);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const ratingColor = race.avgRating <= 5 ? '#ef4444' : race.avgRating <= 10 ? '#f97316' : race.avgRating <= 15 ? '#eab308' : '#22c55e';

  const content = (
    <>
      {/* Circuit silhouette */}
      {circuitUrl && (
        <img src={circuitUrl} alt="" aria-hidden
          className="absolute right-0 top-0 h-full w-2/3 object-contain object-right pointer-events-none select-none transition-opacity group-hover:opacity-30"
          style={{ opacity: cancelled ? 0.06 : done ? 0.12 : 0.15, filter: 'brightness(2)' }} />
      )}

      {/* Cancelled stamp */}
      {cancelled && (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <span className="rotate-[-18deg] border-2 border-f1red text-f1red text-lg font-black tracking-[0.2em] px-4 py-1 rounded opacity-80"
            style={{ textShadow: '0 0 16px rgba(225,6,0,0.4)' }}>CANCELED</span>
        </div>
      )}

      <div className="relative z-10">
        {/* Top row */}
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-zinc-600">R{race.round}</span>
            <span className="text-lg leading-none">{flag}</span>
          </div>
          {cancelled
            ? <span className="text-[10px] bg-f1red/10 text-f1red border border-f1red/30 px-2 py-0.5 rounded-full">Annulé</span>
            : done
            ? <span className="text-[10px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full">Terminé</span>
            : race.isLocked
            ? <span className="text-[10px] bg-orange-900/30 text-orange-400 border border-orange-800/60 px-2 py-0.5 rounded-full">Verrouillé</span>
            : <span className="text-[10px] bg-f1red/10 text-f1red border border-f1red/30 px-2 py-0.5 rounded-full">À venir</span>
          }
        </div>

        {/* Name */}
        <h2 className={`font-black text-base leading-tight mb-0.5 transition-colors ${cancelled ? 'text-zinc-600' : done ? 'text-zinc-300 group-hover:text-white' : 'text-white group-hover:text-f1red'}`}>
          {race.raceName}
        </h2>
        <p className="text-zinc-600 text-xs mb-3 truncate">{race.circuit?.locality}, {race.circuit?.country}</p>

        {/* Winner (if done) */}
        {done && (
          <div className="flex items-center gap-1.5 mb-3 px-2.5 py-1.5 rounded-lg bg-yellow-400/8 border border-yellow-400/20">
            <span className="text-yellow-400 text-xs">🏆</span>
            <span className="text-yellow-400 font-black text-xs uppercase tracking-wide truncate">{race.results.winner}</span>
          </div>
        )}

        {/* Bottom row */}
        <div className="flex items-center justify-between gap-2 border-t border-zinc-800/80 pt-2.5 mt-auto">
          <div className="flex gap-3 text-[11px] text-zinc-600">
            {race.sessions?.qualifying?.date && (
              <span>Q · {formatDate(race.sessions.qualifying.date)}</span>
            )}
            {race.sessions?.race?.date && (
              <span className={done ? 'text-zinc-500' : 'text-zinc-500'}>
                🏁 {formatDate(race.sessions.race.date)}
              </span>
            )}
          </div>
          {race.avgRating != null && (
            <span className="flex items-center gap-1 shrink-0 text-[11px] font-black" style={{ color: ratingColor }}>
              ⭐ {race.avgRating.toFixed(1)}<span className="text-zinc-600 font-normal">/20</span>
            </span>
          )}
        </div>
      </div>
    </>
  );

  const cls = cancelled
    ? 'card relative overflow-hidden opacity-40 cursor-not-allowed select-none flex flex-col'
    : `card relative overflow-hidden hover:border-zinc-600 transition-all group cursor-pointer flex flex-col ${done ? 'hover:bg-zinc-800/30' : 'hover:bg-zinc-800/40'}`;

  return (
    <div ref={ref} className="transition-all duration-500"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(12px)', transitionDelay: `${(idx % 6) * 50}ms` }}>
      {cancelled
        ? <div className={cls}>{content}</div>
        : <Link to={`/grand-prix/${race._id}`} className={cls}>{content}</Link>
      }
    </div>
  );
};

const Calendar = () => {
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get(`/races?season=${SEASON}`)
      .then(res => setRaces(res.data))
      .catch(() => setError('Impossible de charger le calendrier.'))
      .finally(() => setLoading(false));
  }, []);

  const nextRace = races.find(r => isUpcoming(r) && !isCancelled(r));
  const completed = races.filter(r => isCompleted(r)).length;
  const total = races.filter(r => !isCancelled(r)).length;

  const filtered = races.filter(r => {
    if (filter === 'done') return isCompleted(r);
    if (filter === 'upcoming') return isUpcoming(r) || (!isCompleted(r) && !isCancelled(r));
    return true;
  });

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3">
      <div className="w-8 h-8 border-2 border-f1red border-t-transparent rounded-full animate-spin" />
      <p className="text-zinc-500 text-sm">Chargement du calendrier…</p>
    </div>
  );

  if (error) return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="card border-red-900 text-red-400">
        <p>{error}</p>
        <p className="text-sm text-zinc-500 mt-2">Synchronise le calendrier depuis le backend.</p>
      </div>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">

      {/* Header */}
      <div className="mb-6">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-medium mb-1">Saison {SEASON}</p>
        <h1 className="text-3xl font-black text-white">Calendrier <span className="text-f1red">F1</span></h1>
        <p className="text-zinc-500 text-sm mt-1">{races.length} Grands Prix · {completed}/{total} terminés</p>
      </div>

      {/* Season progress bar */}
      {total > 0 && (
        <div className="mb-6">
          <div className="flex justify-between text-[11px] text-zinc-500 mb-1.5">
            <span>{completed} GP terminés</span>
            <span>{total - completed} restants</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-f1red/80 to-f1red rounded-full transition-all duration-1000"
              style={{ width: `${(completed / total) * 100}%` }} />
          </div>
          <div className="flex mt-1 gap-px">
            {Array.from({ length: total }).map((_, i) => (
              <div key={i} className="flex-1 h-0.5 rounded-full"
                style={{ background: i < completed ? '#e10600' : '#27272a' }} />
            ))}
          </div>
        </div>
      )}

      {/* Next race hero */}
      {nextRace && <NextRaceHero race={nextRace} />}

      {/* Filters */}
      <div className="flex gap-2 mb-5">
        {[['all', `Tous (${races.length})`], ['done', `Terminés (${completed})`], ['upcoming', `À venir (${total - completed})`]].map(([f, label]) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${filter === f ? 'bg-f1red text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {races.length === 0 ? (
        <div className="card text-center text-zinc-400 py-12">
          <p className="text-lg mb-2">Aucune course trouvée.</p>
          <p className="text-sm">Synchronise le calendrier depuis le backend.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((race, i) => (
            <RaceCard key={race._id} race={race} idx={i} />
          ))}
        </div>
      )}
    </div>
  );
};

export default Calendar;
